import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { LogIn, UserPlus, MailCheck } from 'lucide-react';
import './styles/LoginPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export const RESET_GENERIC_MESSAGE = "If an account exists for that email, we've sent a reset link. Check your inbox (and spam).";

export default function LoginPage({ setUser }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    // Check for invite token / forgot-password shortcut in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    
    if (token) {
      setInviteToken(token);
      fetchInviteInfo(token);
    }
    if (params.get('forgot') === '1') {
      setShowForgotPassword(true);
    }
  }, []);

  const fetchInviteInfo = async (token) => {
    try {
      const response = await axios.get(`${API}/invitations/${token}`);
      setInviteInfo(response.data);
      setFormData(prev => ({ ...prev, email: response.data.invitation.email }));
      setIsLogin(false); // Switch to register mode for invites
      toast.success(`Invitation from ${response.data.invited_by} to join ${response.data.team}`);
    } catch (error) {
      toast.error('Invalid or expired invitation');
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name };

      // Add invite token if present
      const url = inviteToken 
        ? `${API}${endpoint}?invite_token=${inviteToken}`
        : `${API}${endpoint}`;

      const response = await axios.post(url, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      localStorage.setItem('session_token', response.data.session_token);
      setUser(response.data.user);
      
      if (inviteToken) {
        toast.success('Welcome! You have been added to the team.');
      } else {
        toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully!');
      }
      
      navigate('/dashboard/kanban');
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail.trim() });
      setResetSent(true);
    } catch (error) {
      toast.error('Could not send the reset link right now. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setResetSent(false);
    setResetEmail('');
  };

  const openForgot = () => {
    setResetEmail(formData.email);
    setShowForgotPassword(true);
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/vibely-logo-light.svg" alt="Vibely" className="login-logo" data-testid="login-logo" />
          <p className="tagline">Team tasks, meetings and messaging with voice</p>
        </div>

        <Card className="login-card">
          {showForgotPassword ? (
            <div data-testid="forgot-password-panel">
              <div className="card-header">
                <h2>Forgot password?</h2>
                <p>Enter your email and we'll send you a one-time link to set a new password.</p>
              </div>

              {resetSent ? (
                <div className="reset-confirmation" data-testid="forgot-confirmation">
                  <MailCheck size={32} aria-hidden="true" />
                  <p>{RESET_GENERIC_MESSAGE}</p>
                  <p className="reset-hint">The link works once and expires in 1 hour.</p>
                  <Button type="button" className="submit-button" onClick={backToLogin} data-testid="forgot-back-button">
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleRequestReset} className="login-form">
                  <div className="form-group">
                    <label htmlFor="reset-email">Email</label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button type="submit" className="submit-button" disabled={loading || !resetEmail.trim()} data-testid="forgot-submit-button">
                    {loading ? 'Sending…' : 'Send reset link'}
                  </Button>
                  
                  <div className="toggle-mode">
                    <button type="button" onClick={backToLogin} className="toggle-button" data-testid="forgot-cancel-button">
                      ← Back to sign in
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <>
              <div className="card-header">
                <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
                <p>{isLogin ? 'Sign in to your account' : 'Sign up to get started'}</p>
              </div>

              <button type="button" className="google-button" onClick={handleGoogleSignIn} data-testid="google-signin-button">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.8h12.7c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.2-10.1 7.2-16.8z"/>
                  <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.7 24c0-1.6.3-3.2.8-4.6l-7.9-6.2A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.2z"/>
                  <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z"/>
                </svg>
                Continue with Google
              </button>
              <div className="auth-divider" aria-hidden="true"><span>or</span></div>

              <form onSubmit={handleSubmit} className="login-form">
            {!isLogin && (
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <div className="label-row">
                <label htmlFor="password">Password</label>
                {isLogin && (
                  <button type="button" onClick={openForgot} className="forgot-link" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="submit-button"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? (
                'Please wait...'
              ) : (
                <>
                  {isLogin ? <LogIn size={20} /> : <UserPlus size={20} />}
                  <span>{isLogin ? 'Sign In' : 'Sign Up'}</span>
                </>
              )}
            </Button>
          </form>

          <div className="toggle-mode">
            <p>
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="toggle-button"
                data-testid="toggle-mode-button"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
            </>
          )}
        </Card>

        <div className="features-preview">
          <div className="feature-item">
            <span className="feature-icon">📋</span>
            <span>Kanban Boards</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">👥</span>
            <span>Team Management</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🎙️</span>
            <span>Voice Meetings</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">✅</span>
            <span>Task Tracking</span>
          </div>
        </div>
      </div>
    </div>
  );
}
